import type { JSX } from 'preact'
import { Logo } from './Logo.js'
import { Rail } from './Rail.js'
import { Screen } from './Screen.js'
import { Window } from './Window.js'

/**
 * «элементар ос» — вертикальная лента из четырнадцати экранов.
 *
 * Тексты выверены и лежат здесь дословно: править их — отдельное решение
 * человека, а не побочный эффект вёрстки. Поэтому они не вынесены в словарь и
 * не собираются из кусков — каждый тезис виден в разметке целиком.
 *
 * Материал публичный: лента ведёт к одному действию — открыть финансер. Раньше
 * здесь лежал внутренний концепт для соучастников, и в нём не было ни кнопки,
 * ни призыва; экраны про деньги и про план на три месяца ушли вместе с ним —
 * это разговор с партнёром, а не с человеком, который зашёл впервые.
 */
export function App(): JSX.Element {
  return (
    <main class="os">
      <Rail />

      {/* 00 · Обложка */}
      <Screen
        id="oblozhka"
        num="00"
        name="Обложка"
        foot={
          <>
            <p class="os-footnote">Человек + нейросеть</p>
            <span class="os-hint" aria-hidden="true">
              ↓
            </span>
          </>
        }
      >
        <Logo class="os-logo" />
        <p class="os-label">Операционная среда</p>
        <h1 class="os-title">элементар ос</h1>
        <p class="os-subtitle">Среда, спроектированная после ИИ, а не до него.</p>
      </Screen>

      {/* 01 · Пустое поле */}
      <Screen id="pole" num="01" name="Пустое поле" label="01 · Пустое поле">
        <h2 class="os-thesis">
          Чат — <span class="os-hl">пустое поле</span>.
        </h2>
        <p class="os-lead">
          Мощная модель, а перед человеком — курсор и вопрос «чем тебя занять». Большинство
          закрывает вкладку. Мощность есть, входа в неё нет.
        </p>
      </Screen>

      {/* 02 · Вход */}
      <Screen id="vkhod" num="02" name="Вход" label="02 · Вход">
        <h2 class="os-thesis">Не спроси что угодно. Сделай вот это.</h2>
        <p class="os-lead">
          Задача уже поставлена, решение зашито внутрь. Инструмент под одну работу — вместо поля,
          где начинать надо с нуля.
        </p>
      </Screen>

      {/* 03 · Формула */}
      <Screen id="formula" num="03" name="Формула" label="03 · Формула" accent>
        <h2 class="os-thesis">AGI — это человек плюс нейросеть.</h2>
        <p class="os-lead">
          Не машина, которая однажды проснётся умной. Мощность даёт модель, направление даёт
          человек. Ни один не работает за двоих.
        </p>
      </Screen>

      {/* 04 · Разворот */}
      <Screen id="razvorot" num="04" name="Разворот" label="04 · Разворот">
        <h2 class="os-thesis">
          терминал <span class="os-dim">→</span> мышь <span class="os-dim">→</span>{' '}
          <span class="os-hl">супертерминал</span>
        </h2>
        <p class="os-lead">
          Сначала машиной управляли словами. Потом — мышью: окна, иконки, поиск готового среди
          тысячи. Теперь язык вернулся, но машина отвечает не строчкой — интерфейсом.
        </p>
      </Screen>

      {/* 05 · Экран */}
      <Screen id="ekran" num="05" name="Экран" label="05 · Экран">
        <h2 class="os-thesis">
          Экран — не склад окон. Экран — <span class="os-hl">ответ</span>.
        </h2>
        <p class="os-lead">
          Просишь таблицу — появляется таблица. Нужна одна кнопка — будет одна. Интерфейс не
          выбирают из меню — он собирается под задачу в момент её появления.
        </p>
      </Screen>

      {/* 06 · Движок */}
      <Screen id="dvizhok" num="06" name="Движок" label="06 · Движок">
        <h2 class="os-thesis">
          Движок — расходник. Ценность — <span class="os-hl">в корпусе</span>.
        </h2>
        <p class="os-lead">
          Модель под капотом взаимозаменяема: сегодня одна, завтра другая, дешевле и умнее. Корпус —
          узкое приложение, где применение придумано и собрано. Движок бесформен. Форму даёт корпус.
        </p>
      </Screen>

      {/* 07 · Корпуса */}
      <Screen id="korpusa" num="07" name="Корпуса" label="07 · Корпуса" wide>
        <h2 class="os-thesis os-thesis--sm">Среда собирается из корпусов.</h2>
        <div class="os-windows os-windows--3">
          <Window title="Двери">Открыл — сделал — закрыл. Планер, финансер.</Window>
          <Window title="Слои">Работают незаметно внутри других. Архивер, корректор.</Window>
          <Window title="Мосты">Свой интерфейс поверх чужой системы. Почтер, одинэсер.</Window>
        </div>
      </Screen>

      {/* 08 · Данные */}
      <Screen id="dannye" num="08" name="Данные" label="08 · Данные">
        <h2 class="os-thesis">
          Данные <span class="os-hl">не уезжают</span> в облако.
        </h2>
        <p class="os-lead">
          Всё живёт на устройстве. Наружу уходит только то, что видно. Приватность — не настройка, а
          конструкция.
        </p>
      </Screen>

      {/* 09 · Штурвал */}
      <Screen id="shturval" num="09" name="Штурвал" label="09 · Штурвал">
        <h2 class="os-thesis">
          Среда предлагает. <span class="os-hl">Решает человек</span>.
        </h2>
        <p class="os-lead">
          Ничего не считается в фоне, ничего не уходит без ведома. Работа ИИ — черновик до
          подтверждения. Не автопилот — штурвал.
        </p>
      </Screen>

      {/* 10 · Скилл */}
      <Screen id="skill" num="10" name="Скилл" label="10 · Скилл" accent>
        <h2 class="os-thesis">
          Один файл. <span class="os-mark">Любая модель</span>. Новый корпус.
        </h2>
        <p class="os-lead">
          Скилл — свод правил среды. Отдаёшь его любой нейросети, описываешь задачу — получаешь
          корпус, который выглядит и работает как часть среды. Модель не важна. Важен свод.
        </p>
      </Screen>

      {/* 11 · Метод */}
      <Screen id="metod" num="11" name="Метод" label="11 · Метод">
        <h2 class="os-thesis">
          ОС не проектируют. Она <span class="os-hl">вырастает</span>.
        </h2>
        <p class="os-lead">
          Все настоящие системы начинались с одного работающего инструмента, вокруг которого
          нарастало остальное. Один корпус. Второй — в том же языке. Корпус, собранный чужими
          руками. Оболочка, которая держит их вместе. Слово «ос» — последним.
        </p>
      </Screen>

      {/* 12 · Открыто */}
      <Screen id="otkryto" num="12" name="Открыто" label="12 · Открыто">
        <h2 class="os-thesis">Среда растёт корпусами. Собери свой.</h2>
        <p class="os-lead">
          Элементар — открытый язык, а не витрина готового. Скилл, дизайн-система и корпуса-образцы
          уже есть. Инструмент под свою задачу собирается за вечер — и остаётся в среде.
        </p>
      </Screen>

      {/* 13 · Финал */}
      <Screen
        id="final"
        num="13"
        name="Финал"
        label="13 · Финал"
        foot={<p class="os-footnote">Элементар · Открытая среда</p>}
      >
        <h2 class="os-thesis">Начни с работающего.</h2>
        <p class="os-lead">
          Финансер — первый корпус среды. Разбирает банковскую выписку, показывает картину года,
          считает по запросу. Данные не покидают устройство. Открой, посмотри, как устроен корпус, —
          и собери следующий.
        </p>
        {/* Единственное действие на всей ленте. Обычная ссылка, а не кнопка со
            скриптом: это переход на соседнюю страницу, и он должен работать
            средним щелчком, из контекстного меню и без JS. */}
        <a class="os-cta" href="/финансер/">
          Открыть финансер →
        </a>
        <Logo class="os-logo" />
      </Screen>
    </main>
  )
}
